import classNames from 'classnames';
import fileToArrayBuffer from 'file-to-array-buffer';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Layer as ParseLayer } from '@meitu-graphic/ag-psd';
import { ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { ParsePPTDataToEditorData } from '../../ppt-parse/parse';
import { IParsePPTToClientTemplateResult } from '../../ppt-parse/upload-model/type';
import psdUploadSuccessImg from '../assets/psd-upload-success.png';
import { parsePsdToClientTemplate } from '../core';
import { IFont, IParsePsdToClientTemplateResult, PsdParseWarnType } from '../core/types';
import { InfoProps } from '../type';

enum ResultType {
    'success',
    'error',
    'processing',
    'warn',
}
const Processing: React.FC<{
    file: File;
    setInfos: (infos: InfoProps[]) => void;
    infos: InfoProps[];
    fonts?: IFont[];
    parseDoneCallback: (data: IParsePsdToClientTemplateResult | IParsePPTToClientTemplateResult | null) => void;
    reUploadClick: () => void;

    noReplaceFontFamily?: boolean;
    noParseBgLayer?: boolean;
    parseNoLimit?: boolean;

    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps>;
    uploadFileToOBS: (file: File, extension?: string) => Promise<string>;
}> = ({
    file: psdFile,
    setInfos,
    parseDoneCallback,
    infos,
    fonts = [],
    reUploadClick,
    noReplaceFontFamily = false,
    noParseBgLayer = false,
    parseNoLimit = false,
    parseLayerCallback,
    uploadFileToOBS,
}) => {
    const [resultType, setResultType] = useState(ResultType.processing);

    const progress = useRef(0);
    const [currentProgress, setCurrentProgress] = useState(0);
    const infosRef = useRef(infos);
    const countInterval = useRef<null | NodeJS.Timeout>(null);
    const [result, setResult] = useState<IParsePsdToClientTemplateResult | IParsePPTToClientTemplateResult | null>(
        null,
    );

    useEffect(() => {
        const parseInfoContentElem = document.getElementById('parseInfoContent');

        if (!parseInfoContentElem) {
            return;
        }

        parseInfoContentElem.scrollTo(0, parseInfoContentElem.scrollHeight);
    }, [infos]);
    useEffect(() => {
        if (!psdFile) {
            return;
        }
        setResultType(ResultType.processing);

        const run = async () => {
            countInterval.current = setInterval(() => {
                if (progress.current < 98) {
                    progress.current += 1;
                    setCurrentProgress(progress.current);
                } else {
                    countInterval.current && clearInterval(countInterval.current);
                }
            }, 100);

            try {
                const fileBuffer = await fileToArrayBuffer(psdFile);
                if (psdFile.name.includes('.psd')) {
                    const result = await parsePsdToClientTemplate(
                        psdFile,
                        fileBuffer,
                        psdFile.name,
                        fonts,
                        async (file, extension = 'png') => {
                            const result = await uploadFileToOBS(file, extension);
                            return result;
                        },
                        (info, warnType, layerId) => {
                            infosRef.current = [
                                ...infosRef.current,
                                {
                                    text: info,
                                    infoType: warnType,
                                    id: uuid(),
                                },
                            ];

                            setInfos([...infosRef.current]);
                        },
                        noParseBgLayer,
                        noReplaceFontFamily,
                        parseNoLimit,
                        parseLayerCallback,
                    );
                    setResult(result);
                    const errorInfos = infosRef.current.filter((item) => item.infoType === PsdParseWarnType.Error);
                    const warnFontInfos = infosRef.current.filter((item) => item.text.includes('字体被替换'));
                    if (warnFontInfos.length > 0) {
                        const data = {
                            text: '以下图层字体不在素材中心，已被替换：',
                            infoType: PsdParseWarnType.Warn,
                            id: uuid(),
                        };
                        infosRef.current = [...infosRef.current, data];
                        warnFontInfos.forEach((item) => {
                            infosRef.current = [...infosRef.current, { ...item, id: uuid() }];
                        });
                    }

                    if (errorInfos.length > 0) {
                        const data = {
                            text: 'psd解析过程中出现不规范问题如下(请修改后重传)：',
                            infoType: PsdParseWarnType.Error,
                            id: uuid(),
                        };
                        infosRef.current = [...infosRef.current, data];
                        errorInfos.forEach((item) => {
                            infosRef.current = [...infosRef.current, { ...item, id: uuid() }];
                        });

                        setResultType(ResultType.error);
                        // throw new Error(
                        //     'psd解析过程中出现不规范问题如下(请修改后重传)'
                        // );
                    }

                    if (errorInfos.length === 0 && warnFontInfos.length === 0) {
                        setResultType(ResultType.success);
                    } else if (errorInfos.length === 0 && warnFontInfos.length !== 0) {
                        setResultType(ResultType.warn);
                    }

                    setInfos([...infosRef.current]);
                    progress.current = 100;
                    countInterval.current && clearInterval(countInterval.current);
                    countInterval.current = null;
                    setCurrentProgress(progress.current);

                    if (errorInfos.length === 0) {
                        parseDoneCallback(result);
                    }
                } else {
                    setResultType(ResultType.processing);
                    const result = await ParsePPTDataToEditorData.parse(psdFile, uploadFileToOBS, {
                        onProgress: (progressData: any) => {
                            const { progress, message } = progressData;
                            console.log(progress, message);
                        },
                        onError: (errorInfo: any) => {
                            const { message } = errorInfo;
                            infosRef.current = [
                                ...infosRef.current,
                                {
                                    text: message,
                                    infoType: PsdParseWarnType.Warn,
                                    id: uuid(),
                                },
                            ];
                            setInfos([...infosRef.current]);
                        },
                    });

                    progress.current = 100;
                    countInterval.current && clearInterval(countInterval.current);
                    countInterval.current = null;
                    setCurrentProgress(progress.current);

                    if (result === null) {
                        setResultType(ResultType.error);
                        return;
                    }

                    if (infosRef.current.some((info) => info.infoType === PsdParseWarnType.Error)) {
                        setResultType(ResultType.warn);
                    }

                    setResult({
                        name: psdFile.name,
                        ...result,
                        file: psdFile,
                    });

                    parseDoneCallback({
                        name: psdFile.name,
                        ...result,
                        file: psdFile,
                    });
                }
            } catch (error) {
                console.log('error===', error);
                throw error;
            }
        };

        run();
    }, [psdFile]);

    const editClick = () => {
        parseDoneCallback(result);
    };

    return (
        <div
            className={classNames({
                'progress-wrap': true,
                error: resultType === ResultType.error,
            })}
        >
            <div className="progress-value" style={{ width: `${currentProgress}%` }} />
            <div className="progress">
                <div className="progress-title">
                    <IconFont name="file" fontSize={12} />
                    <span className="name">{psdFile.name}</span>
                </div>

                {currentProgress !== 100 ? <div>上传中 {currentProgress}%</div> : null}

                {currentProgress === 100 ? (
                    <div className="edit-wrap">
                        {resultType === ResultType.error ? (
                            <div className="chooseNewPsd" onClick={reUploadClick}>
                                上传失败， 请重新上传
                            </div>
                        ) : (
                            <div className="edit mr-15" onClick={editClick}>
                                编辑
                            </div>
                        )}
                    </div>
                ) : null}
            </div>

            <div className="title">
                图层信息
                <br />
                <span>黄字报错信息为设置不规范，请点击编辑进入编辑器调整。红字报错信息，需要调整源文件后重新上传</span>
            </div>

            {resultType !== ResultType.success ? (
                <div className="content" id="parseInfoContent">
                    {infos.map((info) => {
                        return (
                            <div
                                key={info.id}
                                className={`item ${
                                    info.infoType === PsdParseWarnType.Error
                                        ? 'error'
                                        : info.infoType === PsdParseWarnType.Warn
                                          ? 'warn'
                                          : 'info'
                                }`}
                            >
                                {info.text}
                            </div>
                        );
                    })}

                    {resultType === ResultType.processing ? <div className="item dotting" /> : null}
                </div>
            ) : (
                <div className="content perfect">
                    <div className="success-tip">创建成功，设计稿图层很规范，希望你再接再厉～</div>
                    <img
                        src={psdUploadSuccessImg}
                        alt="创建成功，设计稿图层很规范，希望你再接再厉～"
                        className="success-img"
                    />
                </div>
            )}
        </div>
    );
};

export default Processing;
